import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
} from "../GuideBlocks.js";

const LeadershipDecisionsSection = ({ heading, accentIndex = 2, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="leadership-decisions"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      Leadership does not just add pressure to a decision. It adds an audience. Every choice you
      make is also a signal — about your priorities, your consistency, and what you will tolerate.
      That is what makes leadership decisions feel heavier than the decision itself often warrants.
    </GuideLead>
    <GuideSectionLabel>Why leaders decide differently</GuideSectionLabel>
    <p className="guide-panel__p">
      A leader rarely has the luxury of deciding in private. Delay reads as indecision. Speed reads
      as carelessness. Changing your mind reads as inconsistency, even when it is the right call.
      That visibility changes the psychology of the decision — and it is exactly where reactive
      decisions do the most damage.
    </p>
    <GuideSectionLabel>Where EIER helps leaders specifically</GuideSectionLabel>
    <p className="guide-panel__p">
      The EIER framework — <strong>Event → Interpretation → Evidence → Response</strong> — matters
      most for leaders at the Response step, because a leader&apos;s response does not just resolve
      one decision. It sets a precedent.
    </p>
    <p className="guide-panel__p">
      Two leaders can face the identical Event — a team member missing a deadline — and land on very
      different Interpretations: &quot;they are not committed&quot; versus &quot;something is
      blocking them I do not know about.&quot; Whichever one goes unexamined becomes the Response,
      and the team learns from that Response far more than from any stated values.
    </p>
    <GuideCallout>
      Check Evidence before the Response becomes precedent. Ask what you actually know versus what
      you are inferring under pressure.
    </GuideCallout>
    <GuideSectionLabel>Deciding well as a leader</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="clarity"
        title="Separate the Event from reputation"
        description='Ask "What actually happened?" before "What will people think if I do X?" The second question, asked first, distorts Interpretation.'
      />
      <GuideCard
        iconKey="energy"
        title="Buy minutes, not days"
        description="Leadership decisions still need speed — but even 90 seconds of naming Interpretation and checking Evidence before responding changes the outcome."
      />
      <GuideCard
        iconKey="leader"
        title="Decide out loud when you can"
        description="Briefly narrating your Event → Interpretation → Evidence → Response process models the thinking you want the team to adopt."
      />
      <GuideCard
        iconKey="awareness"
        title="Watch for pressure-driven precedent"
        description="A Response made from frustration often becomes policy by accident — teams remember what you did under pressure more than what you said in a calm meeting."
      />
    </GuideCardGrid>
    <GuideCallout variant="amber">
      The weight of leadership decisions does not come from the decision being objectively harder.
      It comes from the fact that your Response teaches people what to expect from you next.
    </GuideCallout>
  </GuidePanel>
);

export default LeadershipDecisionsSection;
